<template>
  <div v-if="salesTeam" class="forms-elements">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <Widget v-if="salesTeam">
      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('basicData') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('name')"
            label-for="name"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input v-model="salesTeam.name" type="text" :placeholder="$t('name')" />
          </b-form-group>

          <CommissionEditor
            v-model="salesTeam.commissionByDepth"
            :distribution-mode="salesTeam.distributionMode"
            :current-depth="currentMaxDepth"
            @update:distributionMode="updateDistributionMode"
            @update:isValid="isCommissionValid = $event"
          />
        </b-col>
        <b-col v-if="!isNew && salesTeam._id" lg="6" md="6" sm="12">
          <SalesTeamMembers :team-id="salesTeam._id" :team="salesTeam" />
        </b-col>
      </b-row>

      <!-- Info Section for non-editable fields -->
      <b-row v-if="!isNew">
        <b-col lg="6" md="6" sm="12">
          <hr />
          <b-form-group
            horizontal
            :label="$t('number')"
            label-for="number"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ salesTeam.number.toString().padStart(4, '0') }}
            </div>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('createdAt')"
            label-for="createdAt"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ $dayjs(salesTeam.createdAt).format('DD.MM.YYYY HH:mm') }}
            </div>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('updatedAt')"
            label-for="updatedAt"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ $dayjs(salesTeam.updatedAt).format('DD.MM.YYYY HH:mm') }}
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col lg="12" md="12" sm="12">
          <ButtonBar
            :showDeleteButton="buttons.showDeleteButton"
            :model="salesTeam"
            modelEditRoute="SalesTeamEditPage"
            modelListRoute="SalesTeamsPage"
            modelRouteParamName="number"
            :updateCallback="updateSalesTeam"
            :createCallback="createSalesTeam"
            :deleteCallback="deleteSalesTeam"
            :disabled="buttonsDisabled"
          />
        </b-col>
      </b-row>
    </Widget>
  </div>
</template>

<script>
import Widget from '@/components/Widget/Widget';
import Breadcrumbs from '@/components/Breadcrumbs';
import { mapGetters, mapActions } from 'vuex';
import ButtonBar from '@/components/ButtonBar.vue';
import CommissionEditor from '@/components/SalesTeams/CommissionEditor.vue';
import SalesTeamMembers from '@/components/SalesTeams/SalesTeamMembers.vue';

export default {
  name: 'SalesTeamEditPage',
  components: {
    Widget,
    Breadcrumbs,
    ButtonBar,
    CommissionEditor,
    SalesTeamMembers,
  },
  props: {
    number: {
      required: false,
      default: null,
    },
  },
  data() {
    return {
      salesTeamNumber: null,
      isCommissionValid: true,
    };
  },
  computed: {
    ...mapGetters(['getSalesTeamByNumber', 'getSalesMembers']),
    buttons() {
      return {
        showDeleteButton: this.salesTeam && this.salesTeam.number !== '_new' ? true : false,
      };
    },
    buttonsDisabled() {
      return !this.isCommissionValid;
    },
    salesTeam() {
      return this.getSalesTeamByNumber(this.salesTeamNumber);
    },
    isNew() {
      return this.salesTeam && this.salesTeam.number === '_new';
    },
    currentMaxDepth() {
      // Calculate the maximum depth from the sales hierarchy
      const members = this.getSalesMembers || [];
      if (members.length === 0) {
        return 3; // Default depth for new teams
      }
      const maxDepth = Math.max(...members.map((m) => m.depth || 0));
      return maxDepth + 1; // depth is 0-based, so add 1 for number of levels
    },
    breadcrumbs() {
      return [
        { name: 'Home', route: { name: 'home' } },
        { name: this.$t('salesTeams'), route: { name: 'SalesTeamsPage' } },
        {
          name:
            this.salesTeam && this.salesTeam.number !== '_new' ? this.$t('edit') : this.$t('new'),
        },
      ];
    },
  },
  methods: {
    ...mapActions([
      'initSalesTeam',
      'fetchSalesTeamByNumber',
      'updateSalesTeam',
      'createSalesTeam',
      'deleteSalesTeam',
    ]),
    updateDistributionMode(value) {
      this.salesTeam.distributionMode = value;
    },
  },
  async mounted() {
    if (this.number) {
      this.salesTeamNumber = parseInt(this.number);
      await this.fetchSalesTeamByNumber(this.salesTeamNumber);
    } else {
      await this.initSalesTeam();
      this.salesTeamNumber = '_new';
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.params.number && to.params.number !== this.number) {
      this.salesTeamNumber = parseInt(to.params.number);
      await this.fetchSalesTeamByNumber(this.salesTeamNumber);
      next();
    } else {
      next(false);
    }
  },
};
</script>

<style scoped lang="scss">
legend {
  position: relative;
}
</style>
